import { supabase } from './supabase';
import { sendPasswordResetEmail } from './emailService';
import { verifyPassword, hashPassword } from '../utils/password';

export const adminAuthService = {
  async login(email, password) {
    try {
      if (!email || !password) {
        return { success: false, error: 'Email and password are required' };
      }

      const { data, error } = await supabase
        .from('admin_users')
        .select('id, email, role_id, is_active, password_hash, admin_roles(name, can_edit_stats, can_edit_news, can_manage_users, can_manage_permissions, can_publish_articles, can_manage_stats, can_manage_rulebook)')
        .ilike('email', email)
        .single();

      if (error || !data) {
        return { success: false, error: 'Invalid email or password' };
      }

      if (!data.is_active) {
        return { success: false, error: 'User account is inactive' };
      }

      if (!data.password_hash) {
        return { success: false, error: 'Invalid email or password' };
      }

      const isPasswordValid = await verifyPassword(password, data.password_hash);
      if (!isPasswordValid) {
        return { success: false, error: 'Invalid email or password' };
      }

      const user = data;
      
      const { data: userRoles, error: rolesError } = await supabase
        .from('admin_user_roles')
        .select('admin_roles(id, name, can_edit_stats, can_edit_news, can_manage_users, can_manage_permissions, can_publish_articles, can_manage_stats, can_manage_rulebook)')
        .eq('user_id', user.id);

      const roles = userRoles?.map(ur => ur.admin_roles) || [];
      const primaryRole = data.admin_roles || (roles.length > 0 ? roles[0] : null);

      await supabase
        .from('admin_users')
        .update({ last_login: new Date().toISOString() })
        .eq('id', user.id);

      return {
        success: true,
        user: {
          id: user.id,
          email: user.email,
          roleId: user.role_id,
          role: primaryRole?.name,
          roles: roles.map(r => ({ id: r.id, name: r.name })),
          permissions: primaryRole
        }
      };
    } catch (error) {
      return { success: false, error: 'Authentication failed' };
    }
  },

  async fetchUserPermissions(userId) {
    try {
      const { data: userRolesData, error: rolesError } = await supabase
        .from('admin_user_roles')
        .select('admin_roles(name, can_edit_stats, can_edit_news, can_manage_users, can_manage_permissions, can_publish_articles, can_manage_stats, can_manage_rulebook)')
        .eq('user_id', userId);

      if (rolesError) throw rolesError;

      const roles = userRolesData?.map(ur => ur.admin_roles) || [];

      const { data: allPermissions, error: allPermsError } = await supabase
        .from('admin_permissions')
        .select('*');

      if (allPermsError) throw allPermsError;

      const roleBasedPermissions = [];
      const addedPermissions = new Set();

      roles.forEach(role => {
        if (role.name === 'Super Admin') {
          roleBasedPermissions.push(
            { name: 'edit_stats', description: 'Can edit player statistics', category: 'stats' },
            { name: 'edit_news', description: 'Can edit news articles', category: 'news' },
            { name: 'manage_users', description: 'Can create and manage admin users', category: 'users' },
            { name: 'manage_permissions', description: 'Can assign permissions to users', category: 'users' },
            { name: 'publish_articles', description: 'Can publish news articles', category: 'news' },
            { name: 'manage_stats', description: 'Can input and manage statistics', category: 'stats' },
            { name: 'manage_rulebook', description: 'Can manage league rulebook', category: 'content' }
          );
          return;
        }
        if (role?.can_edit_stats && !addedPermissions.has('edit_stats')) {
          roleBasedPermissions.push({ name: 'edit_stats', description: 'Can edit player statistics', category: 'stats' });
          addedPermissions.add('edit_stats');
        }
        if (role?.can_edit_news && !addedPermissions.has('edit_news')) {
          roleBasedPermissions.push({ name: 'edit_news', description: 'Can edit news articles', category: 'news' });
          addedPermissions.add('edit_news');
        }
        if (role?.can_manage_users && !addedPermissions.has('manage_users')) {
          roleBasedPermissions.push({ name: 'manage_users', description: 'Can create and manage admin users', category: 'users' });
          addedPermissions.add('manage_users');
        }
        if (role?.can_manage_permissions && !addedPermissions.has('manage_permissions')) {
          roleBasedPermissions.push({ name: 'manage_permissions', description: 'Can assign permissions to users', category: 'users' });
          addedPermissions.add('manage_permissions');
        }
        if (role?.can_publish_articles && !addedPermissions.has('publish_articles')) {
          roleBasedPermissions.push({ name: 'publish_articles', description: 'Can publish news articles', category: 'news' });
          addedPermissions.add('publish_articles');
        }
        if (role?.can_manage_stats && !addedPermissions.has('manage_stats')) {
          roleBasedPermissions.push({ name: 'manage_stats', description: 'Can input and manage statistics', category: 'stats' });
          addedPermissions.add('manage_stats');
        }
        if (role?.can_manage_rulebook && !addedPermissions.has('manage_rulebook')) {
          roleBasedPermissions.push({ name: 'manage_rulebook', description: 'Can manage league rulebook', category: 'content' });
          addedPermissions.add('manage_rulebook');
        }
      });

      const { data: explicitPerms, error: permsError } = await supabase
        .from('admin_user_permissions')
        .select('admin_permissions(id, name, description, category)')
        .eq('user_id', userId);

      if (permsError) throw permsError;

      const explicitPermissions = explicitPerms.map(p => p.admin_permissions);
      
      return [...roleBasedPermissions, ...explicitPermissions];
    } catch (error) {
      console.error('Error fetching permissions:', error);
      return [];
    }
  },

  async createAdminUser(email, password, roleId, initialPermissions = [], additionalRoleIds = []) {
    try {
      if (!email || !password) {
        return { success: false, error: 'Email and password are required' };
      }

      const passwordHash = await hashPassword(password);

      const { data, error } = await supabase
        .from('admin_users')
        .insert([{
          email,
          password_hash: passwordHash,
          role_id: roleId,
          is_active: true
        }])
        .select()
        .single();

      if (error) throw error;

      const allRoleIds = [roleId, ...additionalRoleIds];
      const roleData = allRoleIds.map(rid => ({
        user_id: data.id,
        role_id: rid
      }));

      const { error: roleError } = await supabase
        .from('admin_user_roles')
        .insert(roleData);

      if (roleError && roleError.code !== 'UNIQUE_VIOLATION') throw roleError;

      if (initialPermissions.length > 0) {
        const permissionData = initialPermissions.map(permId => ({
          user_id: data.id,
          permission_id: permId
        }));

        const { error: permError } = await supabase
          .from('admin_user_permissions')
          .insert(permissionData);

        if (permError) throw permError;
      }

      return { success: true, user: data };
    } catch (error) {
      return { success: false, error: error.message };
    }
  },

  async updateAdminUser(userId, updates) {
    try {
      const { data, error } = await supabase
        .from('admin_users')
        .update(updates)
        .eq('id', userId)
        .select()
        .single();

      if (error) throw error;
      return { success: true, user: data };
    } catch (error) {
      return { success: false, error: error.message };
    }
  },

  async updateUserRoles(userId, roleIds) {
    try {
      if (!Array.isArray(roleIds) || roleIds.length === 0) {
        return { success: false, error: 'At least one role is required' };
      }

      const { error: deleteError } = await supabase
        .from('admin_user_roles')
        .delete()
        .eq('user_id', userId);

      if (deleteError) throw deleteError;

      const roleData = roleIds.map(roleId => ({
        user_id: userId,
        role_id: roleId
      }));

      const { error: insertError } = await supabase
        .from('admin_user_roles')
        .insert(roleData);

      if (insertError) throw insertError;

      const { error: updateError } = await supabase
        .from('admin_users')
        .update({ role_id: roleIds[0] })
        .eq('id', userId);

      if (updateError) throw updateError;

      return { success: true };
    } catch (error) {
      return { success: false, error: error.message };
    }
  },

  async getAllAdminUsers() {
    try {
      const { data, error } = await supabase
        .from('admin_users')
        .select('id, email, role_id, is_active, created_at, updated_at, last_login, admin_roles(name), admin_user_roles(admin_roles(id, name))');

      if (error) throw error;

      const enrichedData = data.map(user => ({
        ...user,
        roles: user.admin_user_roles?.map(ur => ur.admin_roles) || []
      }));

      return { success: true, users: enrichedData };
    } catch (error) {
      return { success: false, error: error.message };
    }
  },

  async deleteAdminUser(userId) {
    try {
      await supabase
        .from('news')
        .update({ user_id: null })
        .eq('user_id', userId);

      await supabase
        .from('news')
        .update({ last_edited_by_id: null })
        .eq('last_edited_by_id', userId);

      await supabase
        .from('news')
        .update({ published_by_id: null })
        .eq('published_by_id', userId);

      await supabase
        .from('webhooks')
        .update({ created_by: null })
        .eq('created_by', userId);

      const { error } = await supabase
        .from('admin_users')
        .delete()
        .eq('id', userId);

      if (error) throw error;
      return { success: true };
    } catch (error) {
      return { success: false, error: error.message };
    }
  },

  async assignPermissionToUser(userId, permissionId) {
    try {
      const { data, error } = await supabase
        .from('admin_user_permissions')
        .insert([{ user_id: userId, permission_id: permissionId }])
        .select();

      if (error) throw error;
      return { success: true, data };
    } catch (error) {
      return { success: false, error: error.message };
    }
  },

  async removePermissionFromUser(userId, permissionId) {
    try {
      const { error } = await supabase
        .from('admin_user_permissions')
        .delete()
        .eq('user_id', userId)
        .eq('permission_id', permissionId);

      if (error) throw error;
      return { success: true };
    } catch (error) {
      return { success: false, error: error.message };
    }
  },

  async getRoles() {
    try {
      const { data, error } = await supabase
        .from('admin_roles')
        .select('*');

      if (error) throw error;
      return { success: true, roles: data };
    } catch (error) {
      return { success: false, error: error.message };
    }
  },

  async getPermissions() {
    try {
      const { data, error } = await supabase
        .from('admin_permissions')
        .select('*');

      if (error) throw error;
      return { success: true, permissions: data };
    } catch (error) {
      return { success: false, error: error.message };
    }
  },

  async changePassword(userId, currentPassword, newPassword) {
    try {
      if (!currentPassword || !newPassword) {
        return { success: false, error: 'Current and new passwords are required' };
      }

      if (currentPassword === newPassword) {
        return { success: false, error: 'New password must be different from current password' };
      }

      const { data: user, error: userError } = await supabase
        .from('admin_users')
        .select('id, email, password_hash')
        .eq('id', userId)
        .single();

      if (userError || !user) {
        return { success: false, error: 'User not found' };
      }

      const isCurrentPasswordValid = await verifyPassword(currentPassword, user.password_hash);
      if (!isCurrentPasswordValid) {
        return { success: false, error: 'Current password is incorrect' };
      }

      const newPasswordHash = await hashPassword(newPassword);

      const { error: updateError } = await supabase
        .from('admin_users')
        .update({ password_hash: newPasswordHash })
        .eq('id', userId);

      if (updateError) throw updateError;

      return { success: true, message: 'Password changed successfully' };
    } catch (error) {
      return { success: false, error: error.message };
    }
  },

  async requestPasswordReset(email) {
    try {
      const { data: user, error: userError } = await supabase
        .from('admin_users')
        .select('id, email')
        .ilike('email', email)
        .single();

      if (userError || !user) {
        return { success: false, error: 'User not found' };
      }

      const token = Math.random().toString(36).substring(2, 15) + Math.random().toString(36).substring(2, 15);
      const expiresAt = new Date(Date.now() + 24 * 60 * 60 * 1000).toISOString();

      const { error } = await supabase
        .from('password_reset_tokens')
        .insert([{
          token,
          user_id: user.id,
          expires_at: expiresAt,
          is_used: false
        }])
        .select('id, user_id, token, expires_at, is_used');

      if (error) throw error;

      const resetLink = `${window.location.origin || 'http://localhost:3000'}/admin/password-reset?token=${token}`;
      
      try {
        await sendPasswordResetEmail(user.email, token, resetLink);
      } catch (emailError) {
        console.error('Failed to send email but token was created:', emailError);
      }

      return { success: true, message: 'Password reset link sent to email', token };
    } catch (error) {
      console.error('Error requesting password reset:', error);
      return { success: false, error: error.message };
    }
  },

  async forcePasswordReset(targetUserId, adminUserId) {
    try {
      const adminRole = await this.getUserRole(adminUserId);
      if (!adminRole || adminRole.name !== 'Super Admin') {
        return { success: false, error: 'Only Super Admins can force password resets' };
      }

      const targetUser = await supabase
        .from('admin_users')
        .select('id, email, password_hash')
        .eq('id', targetUserId)
        .single();

      if (targetUser.error || !targetUser.data) {
        return { success: false, error: 'User not found' };
      }

      const tempPassword = Math.random().toString(36).substring(2, 10) + Math.random().toString(36).substring(2, 10);
      const hashedPassword = await hashPassword(tempPassword);

      const { error: updateError } = await supabase
        .from('admin_users')
        .update({ password_hash: hashedPassword })
        .eq('id', targetUserId);

      if (updateError) {
        throw new Error(updateError.message);
      }

      const { logsService } = await import('./logsService');
      await logsService.createLog(
        adminUserId,
        (await supabase.from('admin_users').select('email').eq('id', adminUserId).single()).data?.email || 'Unknown',
        'force_password_reset',
        {
          target_user_id: targetUserId,
          target_user_email: targetUser.data.email,
          temp_password_created: true
        }
      );

      return { success: true, message: 'Temporary password created', tempPassword };
    } catch (error) {
      return { success: false, error: error.message };
    }
  },

  async validateResetToken(token) {
    try {
      const { data, error } = await supabase
        .from('password_reset_tokens')
        .select('id, user_id, expires_at, is_used')
        .eq('token', token)
        .single();

      if (error || !data) {
        return { success: false, error: 'Invalid token' };
      }

      if (data.is_used) {
        return { success: false, error: 'Token has already been used' };
      }

      const expiresAt = new Date(data.expires_at);
      if (expiresAt < new Date()) {
        return { success: false, error: 'Token has expired' };
      }

      return { success: true, userId: data.user_id, email: data.email };
    } catch (error) {
      console.error('Error validating reset token:', error);
      return { success: false, error: error.message };
    }
  },

  async resetPassword(token, newPassword) {
    try {
      if (!newPassword) {
        return { success: false, error: 'New password is required' };
      }

      const { data: tokenData, error: tokenError } = await supabase
        .from('password_reset_tokens')
        .select('id, user_id, is_used, expires_at')
        .eq('token', token)
        .single();

      if (tokenError || !tokenData) {
        return { success: false, error: 'Invalid token' };
      }

      if (tokenData.is_used) {
        return { success: false, error: 'Token has already been used' };
      }

      const expiresAt = new Date(tokenData.expires_at);
      if (expiresAt < new Date()) {
        return { success: false, error: 'Token has expired' };
      }

      const newPasswordHash = await hashPassword(newPassword);

      const { error: updateError } = await supabase
        .from('admin_users')
        .update({ password_hash: newPasswordHash })
        .eq('id', tokenData.user_id);

      if (updateError) throw updateError;

      const { error: markUsedError } = await supabase
        .from('password_reset_tokens')
        .update({ is_used: true })
        .eq('id', tokenData.id);

      if (markUsedError) throw markUsedError;

      return { success: true, message: 'Password reset successfully' };
    } catch (error) {
      console.error('Error resetting password:', error);
      return { success: false, error: error.message };
    }
  },

  async userHasPermission(userId, permissionKey) {
    try {
      const { data: userData, error: userError } = await supabase
        .from('admin_users')
        .select('admin_roles(name)')
        .eq('id', userId)
        .single();

      if (userError) throw userError;

      const roleName = userData.admin_roles?.name;

      if (roleName === 'Super Admin') {
        return true;
      }

      const { data: role, error: roleError } = await supabase
        .from('admin_roles')
        .select(permissionKey)
        .eq('name', roleName)
        .single();

      if (roleError) throw roleError;

      return role?.[permissionKey] || false;
    } catch (error) {
      console.error('Error checking permission:', error);
      return false;
    }
  },

  async userCanManageMedia(userId) {
    return this.userHasPermission(userId, 'can_manage_media');
  },

  async userCanManageStats(userId) {
    return this.userHasPermission(userId, 'can_manage_stats');
  },

  async userCanPublishArticles(userId) {
    return this.userHasPermission(userId, 'can_publish_articles');
  },

  async userCanEditArticles(userId) {
    return this.userHasPermission(userId, 'can_edit_articles');
  },

  async userCanDeleteArticles(userId) {
    return this.userHasPermission(userId, 'can_delete_articles');
  },

  async userCanEditStats(userId) {
    return this.userHasPermission(userId, 'can_edit_stats');
  },

  async userCanDeleteStats(userId) {
    return this.userHasPermission(userId, 'can_delete_stats');
  },

  async userCanManageConferences(userId) {
    return this.userHasPermission(userId, 'can_manage_conferences');
  },

  async userCanManageUsers(userId) {
    return this.userHasPermission(userId, 'can_manage_users');
  },

  async userCanManageRoles(userId) {
    return this.userHasPermission(userId, 'can_manage_roles');
  },

  async getUserRole(userId) {
    try {
      const { data, error } = await supabase
        .from('admin_users')
        .select('admin_roles(name, id)')
        .eq('id', userId)
        .single();

      if (error) throw error;

      return data.admin_roles;
    } catch (error) {
      console.error('Error fetching user role:', error);
      return null;
    }
  },

  async getUserRoles(userId) {
    try {
      const { data, error } = await supabase
        .from('admin_user_roles')
        .select('admin_roles(id, name)')
        .eq('user_id', userId);

      if (error) throw error;

      return data?.map(ur => ur.admin_roles) || [];
    } catch (error) {
      console.error('Error fetching user roles:', error);
      return [];
    }
  },

  async getUserPermissions(userId) {
    try {
      const { data, error } = await supabase
        .from('admin_user_permissions')
        .select('permission_id')
        .eq('user_id', userId);

      if (error) throw error;
      return data.map(p => p.permission_id);
    } catch (error) {
      console.error('Error fetching user permissions:', error);
      return [];
    }
  },

  async updateUserPermissions(userId, permissionIds) {
    try {
      const { error: deleteError } = await supabase
        .from('admin_user_permissions')
        .delete()
        .eq('user_id', userId);

      if (deleteError) throw deleteError;

      if (permissionIds.length > 0) {
        const permissionData = permissionIds.map(permId => ({
          user_id: userId,
          permission_id: permId
        }));

        const { error: insertError } = await supabase
          .from('admin_user_permissions')
          .insert(permissionData);

        if (insertError) throw insertError;
      }

      return { success: true };
    } catch (error) {
      return { success: false, error: error.message };
    }
  },

  getRoleHierarchy() {
    const hierarchy = {
      'Super Admin': 10,
      'League Management': 9,
      'Head of Media': 8,
      'Head of Statistics': 8,
      'Editor': 7,
      'Stats Manager': 6,
      'News Manager': 5,
      'Media Analyst': 4,
      'Statistics Analyst': 3,
      'Viewer': 1
    };
    return hierarchy;
  },

  getRoleLevel(roleName) {
    const hierarchy = this.getRoleHierarchy();
    return hierarchy[roleName] || 0;
  },

  canManageRole(currentUserRoleName, targetUserRoleName) {
    const currentLevel = this.getRoleLevel(currentUserRoleName);
    const targetLevel = this.getRoleLevel(targetUserRoleName);
    return currentLevel > targetLevel;
  },

  async canCurrentUserManageUser(managerId, targetUserId) {
    try {
      const managerRoles = await this.getUserRoles(managerId);
      const targetRoles = await this.getUserRoles(targetUserId);

      if (managerRoles.length === 0 || targetRoles.length === 0) {
        return false;
      }

      const getHighestLevel = (roles) => {
        return Math.max(...roles.map(r => this.getRoleLevel(r.name)));
      };

      const managerLevel = getHighestLevel(managerRoles);
      const targetLevel = getHighestLevel(targetRoles);

      return managerLevel > targetLevel;
    } catch (error) {
      console.error('Error checking if user can manage:', error);
      return false;
    }
  },


};
